/**
 * API Utilities for Besalla Cashier
 * Provides endpoint building functionality similar to Flutter's AuthUtils.buildEndpoint()
 */

/**
 * Get employee type from stored employee data
 * @returns 'cashier' | 'customer_service' | 'customerService'
 */
export function getEmployeeType(): string {
  try {
    const employeeDataStr = localStorage.getItem('employee_data')
    if (!employeeDataStr) {
      return 'cashier' // default value
    }

    const employeeData = JSON.parse(employeeDataStr)
    
    // Check in employee object first
    let userType = ''
    if (employeeData.employee) {
      const employee = employeeData.employee
      userType = employee.user_type || employee.type || ''
    }
    
    // Fallback to direct check in employeeData
    if (!userType) {
      userType = employeeData.user_type || employeeData.type || ''
    }

    return userType || 'cashier'
  } catch (e) {
    return 'cashier' // default value
  }
}

/**
 * Check if current user is Customer Service
 * @returns boolean
 */
export function isCustomerServiceUser(): boolean {
  try {
    const employeeType = getEmployeeType()
    const userType = employeeType.toString().toLowerCase()
    
    return (
      userType.includes('cs') ||
      userType.includes('customer_service') ||
      userType === 'customer_service'
    )
  } catch (e) {
    return false
  }
}

/**
 * Get customer ID from CS Flow state or Customer state
 * @returns customer ID or null
 */
export function getCustomerId(): number | null {
  try {
    // Try to get from CS Flow state (Redux)
    const csFlowState = localStorage.getItem('csFlow')
    if (csFlowState) {
      const parsed = JSON.parse(csFlowState)
      if (parsed.customerId && parsed.customerId > 0) {
        return parsed.customerId
      }
    }

    // Try to get from customer state (Redux)
    const customerState = localStorage.getItem('customer')
    if (customerState) {
      const parsed = JSON.parse(customerState)
      if (parsed.customerId && parsed.customerId > 0) {
        return parsed.customerId
      }
    }

    // Try to get from csFlowSlice state directly
    // This is a fallback - in actual usage, we should get it from Redux store
    return null
  } catch (e) {
    return null
  }
}

/**
 * Build API endpoint with dynamic employee type
 * Replaces /cashier/ with /customerService/ when user is CS
 * 
 * @param endpoint - The original endpoint (e.g., '/cashier/carts/user/123')
 * @param customerId - Optional customer ID for CS cart operations
 * @returns The modified endpoint
 */
export function buildEndpoint(endpoint: string, customerId?: number | null): string {
  const employeeType = getEmployeeType()

  // Handle special case for customer_service type
  let replacementType = employeeType
  if (employeeType === 'customer_service') {
    replacementType = 'customerService'
  }

  // Special handling for CS cart endpoints
  if (employeeType === 'customer_service' && endpoint.includes('/cashier/carts/items')) {
    // For CS mode, we need to get the customer ID and use the correct endpoint
    const csCustomerId = customerId ?? getCustomerId()
    
    if (csCustomerId && csCustomerId > 0) {
      return `/customerService/carts/users/${csCustomerId}`
    }

    // If we can't get customer ID, throw error to prevent incorrect API call
    throw new Error('Customer not selected for CS cart operation')
  }

  // Special handling for CS cart endpoints that need customer ID
  if (employeeType === 'customer_service' && endpoint.includes('/cashier/carts/user/')) {
    // Replace endpoint for CS mode
    return endpoint.replace('/cashier/carts/user/', `/customerService/carts/user/`)
  }

  // Regular replacement: /cashier/ -> /customerService/ or /cashier/
  const result = endpoint.replace('/cashier/', `/${replacementType}/`)

  return result
}

/**
 * Get platform header value based on user type
 * @returns 'cashier' | 'customer_service'
 */
export function getPlatformHeader(): string {
  if (isCustomerServiceUser()) {
    return 'customer_service'
  }
  return 'cashier'
}







